import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api-client';
import { getMockPublicSession } from '@/lib/mock-public-session';
import { CheckoutShell } from './CheckoutShell';
import { StatusChip } from './StatusChip';
import { CountdownTimer, formatExpiry } from './CountdownTimer';
import { RailTab } from './RailTab';
import { CryptoRailPanel } from './CryptoRailPanel';
import { WhopRailPanel } from './WhopRailPanel';
import { PromoCodeApplyBox } from './PromoCodeApplyBox';
import { ApiRequestError } from '@/lib/api-client';
import { toast } from 'sonner';
import { AlertTriangle, ArrowRight, Coins, CreditCard, Link2Off, Loader2, RefreshCw } from 'lucide-react';

interface PublicSession {
  sessionId: string;
  appid: string;
  tenantName?: string;
  amountUsdCents: number;
  accepts: string;
  chosenRail?: string;
  // pdaAddress: user's resolved PDA base58 address for QR display (computed at request time)
  pdaAddress?: string | null;
  paymentAddress?: string | null;
  solanaPayReference?: string;
  whopCheckoutUrl?: string;
  status: string;
  fulfillmentStatus?: string;
  webhookDeliveryStatus?: string;
  expiresAt: number;
  successRedirectUrl?: string;
  nativeAmount?: string;
  // Promo discount fields (present only when a promo code was applied at session create).
  promoCode?: string;
  originalAmountUsdCents?: number;
  discountPercent?: number;
}

const CRYPTO_RAILS = new Set(['usdc', 'sol', 'usdt']);
const AUTO_POLL_STATUSES = new Set(['pending', 'rail_chosen', 'payment_detected']);
const TERMINAL_STATUSES = new Set(['settled', 'expired', 'failed']);
const FAST_POLL_MS = 5000;
const MEDIUM_POLL_MS = 15000;
const SLOW_POLL_MS = 30000;
const LONG_LIVED_POLL_MS = 60000;
const LONG_LIVED_SESSION_SECONDS = 30 * 24 * 60 * 60;

type FetchSessionOptions = {
  resetPoll?: boolean;
};

function normalizeRails(accepts: string): string[] {
  return accepts
    .split(',')
    .map((rail) => rail.trim().toLowerCase())
    .filter(Boolean);
}

function isTerminalStatus(status?: string): boolean {
  return TERMINAL_STATUSES.has((status ?? '').toLowerCase());
}

function fulfillmentPending(session: PublicSession | null): boolean {
  return (session?.status ?? '').toLowerCase() === 'settled'
    && (session?.fulfillmentStatus ?? 'delivered') === 'pending';
}

function checkoutComplete(session: PublicSession | null): boolean {
  if ((session?.status ?? '').toLowerCase() !== 'settled') return false;
  const fulfillment = session?.fulfillmentStatus ?? 'delivered';
  return fulfillment === 'delivered' || fulfillment === 'not_required';
}

function isWhopSuccessReturn(search: string): boolean {
  const params = new URLSearchParams(search);
  const successStatus = [params.get('checkout_status'), params.get('status')]
    .some((value) => ['success', 'succeeded', 'complete', 'completed', 'paid'].includes((value ?? '').toLowerCase()));
  const hasWhopReturnMarker = ['receipt_id', 'payment_id', 'state_id', 'checkout_status']
    .some((key) => params.has(key));

  return successStatus && hasWhopReturnMarker;
}

function shouldAutoPoll(session: PublicSession | null): boolean {
  if (!session) return false;
  if (fulfillmentPending(session)) return true;
  if (isTerminalStatus(session.status)) return false;
  const status = (session.status ?? '').toLowerCase();
  if (!AUTO_POLL_STATUSES.has(status)) return false;
  const expiresAt = Number(session.expiresAt);
  if (Number.isFinite(expiresAt) && expiresAt <= Math.floor(Date.now() / 1000)) return false;
  return true;
}

function hasLongLivedExpiry(session: PublicSession): boolean {
  const expiresAt = Number(session.expiresAt);
  if (!Number.isFinite(expiresAt)) return false;
  return expiresAt - Math.floor(Date.now() / 1000) > LONG_LIVED_SESSION_SECONDS;
}

function nextPollDelayMs(session: PublicSession, pollCount: number): number {
  if (fulfillmentPending(session)) return FAST_POLL_MS;
  if (hasLongLivedExpiry(session)) return LONG_LIVED_POLL_MS;
  if ((session.status ?? '').toLowerCase() === 'payment_detected') return FAST_POLL_MS;
  if (pollCount < 6) return FAST_POLL_MS;
  if (pollCount < 18) return MEDIUM_POLL_MS;
  return SLOW_POLL_MS;
}

export const CheckoutPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState<PublicSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeRail, setActiveRail] = useState<string>('');
  const [switchingRail, setSwitchingRail] = useState<string | null>(null);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [methodChoice, setMethodChoice] = useState<'crypto' | null>(null);
  const [openingWhop, setOpeningWhop] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollCountRef = useRef(0);
  const latestSessionRef = useRef<PublicSession | null>(null);
  const sessionIdRef = useRef<string | undefined>(sessionId);
  const inFlightSessionRef = useRef<string | null>(null);
  const fetchSessionRef = useRef<(options?: FetchSessionOptions) => Promise<void>>(async () => {});

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const scheduleNextPoll = useCallback((nextSession: PublicSession | null) => {
    stopPolling();
    if (!nextSession || !shouldAutoPoll(nextSession)) return;
    if (typeof document !== 'undefined' && document.hidden) return;

    const delay = nextPollDelayMs(nextSession, pollCountRef.current);
    pollRef.current = setTimeout(() => {
      pollCountRef.current += 1;
      void fetchSessionRef.current();
    }, delay);
  }, [stopPolling]);

  const fetchSession = useCallback(async (options: FetchSessionOptions = {}) => {
    const requestSessionId = sessionId;
    if (!requestSessionId) return;
    if (inFlightSessionRef.current === requestSessionId) return;
    if (options.resetPoll) pollCountRef.current = 0;
    inFlightSessionRef.current = requestSessionId;

    try {
      const data = await api.get(`/api/v1/sessions/${requestSessionId}/public`);
      if (sessionIdRef.current !== requestSessionId) return;
      latestSessionRef.current = data;
      setSession(data);
      setNotFound(false);
      setLoadError(null);
      if (!activeRail && data.accepts) {
        const rails = normalizeRails(data.accepts);
        setActiveRail(data.chosenRail ?? rails[0]);
      }
      if (checkoutComplete(data)) {
        stopPolling();
        navigate(`/pay/${requestSessionId}/success`);
      } else if (data.status === 'expired' || data.status === 'failed') {
        stopPolling();
        navigate(`/pay/${requestSessionId}/expired`);
      } else {
        scheduleNextPoll(data);
      }
    } catch (e: any) {
      const fallback = await getMockPublicSession(requestSessionId);
      if (sessionIdRef.current !== requestSessionId) return;
      if (fallback) {
        latestSessionRef.current = fallback;
        setSession(fallback);
        setNotFound(false);
        setLoadError(null);
        if (!activeRail && fallback.accepts) {
          const rails = normalizeRails(fallback.accepts);
          setActiveRail(fallback.chosenRail ?? rails[0]);
        }
        if (checkoutComplete(fallback as PublicSession)) {
          stopPolling();
          navigate(`/pay/${requestSessionId}/success`);
        } else if (fallback.status === 'expired' || fallback.status === 'failed') {
          stopPolling();
          navigate(`/pay/${requestSessionId}/expired`);
        } else {
          scheduleNextPoll(fallback);
        }
        return;
      }
      // Stop polling on 404 — no point retrying a non-existent session
      const status = e?.status ?? e?.response?.status;
      if (status === 404) {
        setNotFound(true);
        setLoadError(null);
        stopPolling();
      } else if (!latestSessionRef.current) {
        setLoadError(e?.message || 'The checkout backend is unavailable. Try again in a moment.');
        stopPolling();
      } else {
        scheduleNextPoll(latestSessionRef.current);
      }
      console.error('Failed to fetch session', e);
    } finally {
      if (inFlightSessionRef.current === requestSessionId) {
        inFlightSessionRef.current = null;
      }
      if (sessionIdRef.current === requestSessionId) {
        setLoading(false);
      }
    }
  }, [sessionId, activeRail, navigate, stopPolling, scheduleNextPoll]);

  useEffect(() => {
    fetchSessionRef.current = fetchSession;
  }, [fetchSession]);

  useEffect(() => {
    if (!sessionId || !isWhopSuccessReturn(location.search)) return;
    stopPolling();
    navigate(`/pay/${sessionId}/success${location.search}`, { replace: true });
  }, [sessionId, location.search, navigate, stopPolling]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
    latestSessionRef.current = null;
    inFlightSessionRef.current = null;
    pollCountRef.current = 0;
    stopPolling();
    setSession(null);
    setLoading(true);
    setNotFound(false);
    setLoadError(null);
    fetchSessionRef.current({ resetPoll: true });
    return stopPolling;
  }, [sessionId]); // eslint-disable-line

  useEffect(() => {
    setMethodChoice(null);
    setLoadError(null);
    setNotFound(false);
  }, [sessionId]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
        return;
      }
      if (shouldAutoPoll(latestSessionRef.current)) {
        void fetchSessionRef.current({ resetPoll: true });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [stopPolling]);

  const handleSelectRail = async (rail: string) => {
    if (!sessionId || !session || switchingRail === rail) return;
    const previousRail = activeRail;
    setSwitchingRail(rail);
    try {
      const data = (await api.post(`/api/v1/sessions/${sessionId}/select-rail`, { rail })) as PublicSession;
      latestSessionRef.current = data;
      pollCountRef.current = 0;
      setSession(data);
      setNotFound(false);
      setActiveRail(data.chosenRail ?? rail);
      if (checkoutComplete(data)) {
        stopPolling();
        navigate(`/pay/${sessionId}/success`);
      } else {
        scheduleNextPoll(data);
      }
    } catch (e) {
      console.error(e);
      setActiveRail(previousRail);
      toast.error('Failed to select payment method');
    } finally {
      setSwitchingRail(null);
    }
  };

  const handleOpenWhop = async () => {
    if (!sessionId || openingWhop) return;
    setOpeningWhop(true);
    try {
      const res = await api.post(`/api/v1/whop-checkout/${sessionId}`);
      const url = res.whopCheckoutUrl ?? res.url;
      if (!url) throw new Error('Whop checkout URL was not returned');
      window.location.href = url;
    } catch (e: any) {
      toast.error(e?.message ?? 'Failed to open card checkout');
      setOpeningWhop(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!sessionId || !session || confirmingPayment) return;
    setConfirmingPayment(true);
    try {
      const data = (await api.post(
        `/api/v1/sessions/${sessionId}/confirm-payment`,
        {}
      )) as Partial<PublicSession> & { status?: string; successRedirectUrl?: string };
      const merged = latestSessionRef.current
        ? { ...latestSessionRef.current, ...data }
        : (data as PublicSession);
      latestSessionRef.current = merged;
      setSession(merged);
      if (checkoutComplete(merged)) {
        stopPolling();
        navigate(`/pay/${sessionId}/success`);
      } else {
        await fetchSession();
      }
    } catch (e) {
      console.error(e);
      toast.error('Payment is not ready to confirm yet');
    } finally {
      setConfirmingPayment(false);
    }
  };

  // Payer-side promo apply. Merges the response into local state synchronously so a stale poll
  // can't briefly revert the discount; the existing checkoutComplete effect handles the $0
  // navigation. Returns the result so the input box can render inline error copy.
  const handleApplyPromo = useCallback(
    async (rawCode: string): Promise<{ ok: true } | { ok: false; errorCode?: string }> => {
      if (!sessionId) return { ok: false, errorCode: 'session_not_payable' };
      try {
        const data = (await api.post(
          `/api/v1/sessions/${sessionId}/apply-promo`,
          { promoCode: rawCode },
        )) as PublicSession;
        latestSessionRef.current = data;
        pollCountRef.current = 0;
        setSession(data);
        if (checkoutComplete(data)) {
          stopPolling();
          navigate(`/pay/${sessionId}/success`);
        } else {
          scheduleNextPoll(data);
        }
        return { ok: true };
      } catch (e) {
        if (e instanceof ApiRequestError && e.status === 400) {
          return { ok: false, errorCode: e.message };
        }
        console.error('Failed to apply promo code', e);
        toast.error('Could not apply promo code');
        return { ok: false };
      }
    },
    [sessionId, navigate, stopPolling, scheduleNextPoll],
  );

  if (loading) {
    return (
      <CheckoutShell>
        <div className='flex-1 flex items-center justify-center' style={{ color: 'var(--pp-text-subtle)' }}>
          Loading…
        </div>
      </CheckoutShell>
    );
  }

  if (loadError && !session) {
    return (
      <CheckoutShell>
        <div className='flex-1 flex items-start justify-center p-4 md:p-8'>
          <div className='w-full max-w-3xl flex flex-col md:flex-row items-start gap-4 md:gap-5'>
            <div
              className='flex flex-col gap-4 p-5 md:p-6 rounded-xl border flex-shrink-0 md:w-72'
              style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)' }}
            >
              <div
                className='w-10 h-10 rounded-full flex items-center justify-center'
                style={{ background: '#FEF6EE' }}
              >
                <AlertTriangle size={18} style={{ color: 'var(--pp-warning)' }} />
              </div>

              <div>
                <h1
                  aria-level={1}
                  className='text-2xl font-semibold leading-tight'
                  style={{ color: 'var(--pp-text)', letterSpacing: '-0.02em' }}
                >
                  Checkout is temporarily unavailable
                </h1>
                <p className='text-sm mt-1.5 leading-relaxed' style={{ color: 'var(--pp-text-muted)' }}>
                  The payment session exists, but Poofy Pay could not reach its checkout service.
                </p>
              </div>

              {sessionId && (
                <div
                  className='text-xs border-t pt-3 flex flex-col gap-1.5'
                  style={{ borderColor: 'var(--pp-border)', color: 'var(--pp-text-subtle)' }}
                >
                  <div className='flex justify-between'>
                    <span className='uppercase tracking-wide text-[10px] font-medium'>Reference</span>
                    <span style={{ fontFamily: 'var(--pp-mono)' }}>{sessionId}</span>
                  </div>
                </div>
              )}
            </div>

            <div
              className='flex-1 flex flex-col items-center justify-center gap-4 p-5 md:p-6 rounded-xl border'
              style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)', minHeight: '14rem' }}
            >
              <p className='text-sm text-center max-w-md' style={{ color: 'var(--pp-text-muted)' }}>
                {loadError}
              </p>
              <button
                type='button'
                onClick={() => {
                  setLoading(true);
                  setLoadError(null);
                  fetchSession({ resetPoll: true });
                }}
                className='inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold'
                style={{ background: 'var(--pp-action)', color: 'white' }}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </CheckoutShell>
    );
  }

  if (notFound || !session) {
    return (
      <CheckoutShell>
        <div className='flex-1 flex items-start justify-center p-4 md:p-8'>
          <div className='w-full max-w-3xl flex flex-col md:flex-row items-start gap-4 md:gap-5'>
            {/* Left: not-found info card */}
            <div
              className='flex flex-col gap-4 p-5 md:p-6 rounded-xl border flex-shrink-0 md:w-72'
              style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)' }}
            >
              <div
                className='w-10 h-10 rounded-full flex items-center justify-center'
                style={{ background: 'var(--pp-subtle)' }}
              >
                <Link2Off size={18} style={{ color: 'var(--pp-text-subtle)' }} />
              </div>

              <div>
                <h1
                  aria-level={1}
                  className='text-2xl font-semibold leading-tight'
                  style={{ color: 'var(--pp-text)', letterSpacing: '-0.02em' }}
                >
                  Checkout link was not found
                </h1>
                <p className='text-sm mt-1.5 leading-relaxed' style={{ color: 'var(--pp-text-muted)' }}>
                  Request a new link from the app
                </p>
              </div>

              {sessionId && (
                <div
                  className='text-xs border-t pt-3 flex flex-col gap-1.5'
                  style={{ borderColor: 'var(--pp-border)', color: 'var(--pp-text-subtle)' }}
                >
                  <div className='flex justify-between'>
                    <span className='uppercase tracking-wide text-[10px] font-medium'>Reference</span>
                    <span style={{ fontFamily: 'var(--pp-mono)' }}>{sessionId}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Right: empty panel matching layout */}
            <div
              className='flex-1 flex flex-col items-center justify-center gap-3 p-5 md:p-6 rounded-xl border'
              style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)', opacity: 0.55, minHeight: '14rem' }}
            >
              <p className='text-sm text-center max-w-xs' style={{ color: 'var(--pp-text-muted)' }}>
                No payment session is available for this link.
              </p>
            </div>
          </div>
        </div>
      </CheckoutShell>
    );
  }

  const rails = normalizeRails(session.accepts);
  const cryptoRails = rails.filter((rail) => CRYPTO_RAILS.has(rail));
  const hasWhop = rails.includes('whop');
  const chosenCryptoRail = session.chosenRail && CRYPTO_RAILS.has(session.chosenRail) ? session.chosenRail : null;
  const cryptoFlowActive = methodChoice === 'crypto' || !!chosenCryptoRail;
  const showMethodChoice = hasWhop && cryptoRails.length > 0 && !cryptoFlowActive && session.chosenRail !== 'whop';
  const usdStr = (session.amountUsdCents / 100).toFixed(2);
  const hasDiscount = typeof session.discountPercent === 'number' && session.discountPercent > 0;
  const originalUsdStr =
    typeof session.originalAmountUsdCents === 'number'
      ? (session.originalAmountUsdCents / 100).toFixed(2)
      : null;
  const isUrgent = session.expiresAt - Math.floor(Date.now() / 1000) <= 300;
  const expiryText = formatExpiry(session.expiresAt);
  const displayRail = switchingRail ?? activeRail;
  const selectedCryptoRail = cryptoRails.includes(displayRail) ? displayRail : (chosenCryptoRail ?? cryptoRails[0] ?? displayRail);
  const panelRail = cryptoFlowActive ? selectedCryptoRail : displayRail;
  const visibleRails = cryptoFlowActive ? cryptoRails : rails;
  const waitingForFulfillment = session.status === 'settled' && !checkoutComplete(session);
  // Show the promo input only on payable, no-promo, unexpired sessions. Hidden in
  // terminal/applied/expired and during the waiting-for-fulfillment / Whop-return flows.
  const canApplyPromo =
    !waitingForFulfillment &&
    !session.promoCode &&
    (session.status === 'pending' || session.status === 'rail_chosen') &&
    session.expiresAt > Math.floor(Date.now() / 1000);

  const handleChooseCrypto = async () => {
    setMethodChoice('crypto');
    if (selectedCryptoRail && activeRail !== selectedCryptoRail) {
      await handleSelectRail(selectedCryptoRail);
    }
  };

  return (
    <CheckoutShell>
      <div className='flex-1 flex items-start justify-center p-4 md:p-8'>
        <div className='w-full max-w-3xl flex flex-col md:flex-row items-start gap-4 md:gap-5'>

          {/* Left card — session info */}
          <div
            className='flex flex-col gap-4 p-5 md:p-6 rounded-xl border flex-shrink-0 md:w-72'
            style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)' }}
          >
            <div>
              <div
                className='text-sm font-medium mb-1.5'
                style={{ color: 'var(--pp-text-muted)' }}
              >
                {session.tenantName ?? 'Merchant'}
              </div>
              <div
                className='font-semibold tabular-nums'
                style={{
                  color: 'var(--pp-text)',
                  letterSpacing: '-0.035em',
                  fontSize: '34px',
                  lineHeight: 1.05,
                }}
              >
                ${usdStr}
              </div>
              {hasDiscount && (
                <div className='mt-2 flex items-center gap-2 flex-wrap' data-testid='checkout-discount'>
                  <span
                    className='inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium tracking-wide'
                    style={{ background: '#ECFDF3', color: 'var(--pp-settled)' }}
                  >
                    {session.promoCode ? `${session.promoCode} · ` : ''}{session.discountPercent}% off
                  </span>
                  {originalUsdStr && (
                    <span className='text-xs line-through' style={{ color: 'var(--pp-text-subtle)' }}>
                      ${originalUsdStr}
                    </span>
                  )}
                </div>
              )}
              {session.amountUsdCents === 0 && (
                <div className='text-xs mt-1 font-medium' style={{ color: 'var(--pp-settled)' }} data-testid='checkout-no-payment'>
                  No payment required
                </div>
              )}
              <div className='text-xs mt-2' style={{ color: 'var(--pp-text-subtle)' }}>
                Secure checkout by pay.poofy.ai
              </div>
            </div>

            <div className='flex items-center gap-2 flex-wrap'>
              <StatusChip status={session.status} />
              {isUrgent && session.status !== 'settled' && (
                <div className='flex items-center gap-1 text-xs' style={{ color: 'var(--pp-warning)' }}>
                  Expires <CountdownTimer expiresAt={session.expiresAt} />
                </div>
              )}
            </div>

            {!isUrgent && (
              <div className='text-xs' style={{ color: 'var(--pp-text-subtle)' }}>
                {expiryText === 'No expiry' ? 'No expiry' : (
                  <>
                    Expires <CountdownTimer expiresAt={session.expiresAt} />
                  </>
                )}
              </div>
            )}

            <div
              className='text-xs border-t pt-3 flex flex-col gap-1.5'
              style={{ borderColor: 'var(--pp-border)', color: 'var(--pp-text-subtle)' }}
            >
              <div className='flex justify-between'>
                <span>Session</span>
                <span style={{ fontFamily: 'var(--pp-mono)' }}>{session.sessionId.slice(0, 14)}…</span>
              </div>
            </div>
          </div>

          {/* Right card — rail selection & payment panel */}
          <div
            className='flex-1 flex flex-col gap-4 p-5 md:p-6 rounded-xl border'
            style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)' }}
          >
            {waitingForFulfillment ? (
              <FulfillmentPanel
                failed={session.fulfillmentStatus === 'failed'}
                onRefresh={() => fetchSession({ resetPoll: true })}
              />
            ) : showMethodChoice ? (
              <PaymentMethodChoice
                amountUsd={usdStr}
                merchant={session.tenantName ?? 'Merchant'}
                onCrypto={handleChooseCrypto}
                onCard={handleOpenWhop}
                openingCard={openingWhop}
              />
            ) : (
              <>
                {/* Promo apply box — payable + no-promo + unexpired only */}
                {canApplyPromo && (
                  <PromoCodeApplyBox onApply={handleApplyPromo} />
                )}

                {/* Rail tabs */}
                {visibleRails.length > 1 && (
                  <div className='flex gap-2'>
                    {visibleRails.map((rail) => (
                      <RailTab
                        key={rail}
                        rail={rail}
                        active={panelRail === rail}
                        onClick={() => handleSelectRail(rail)}
                      />
                    ))}
                  </div>
                )}

                {/* Payment panel */}
                {panelRail === 'whop' ? (
                  <WhopRailPanel
                    session={session}
                    onSelectOther={cryptoRails.length ? handleChooseCrypto : undefined}
                  />
                ) : (
                  <CryptoRailPanel
                    session={session}
                    rail={panelRail}
                    onRefresh={() => fetchSession({ resetPoll: true })}
                    isQuoteLoading={!!switchingRail && switchingRail === panelRail && panelRail !== 'whop'}
                    onConfirmPayment={handleConfirmPayment}
                    confirmingPayment={confirmingPayment}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </CheckoutShell>
  );
};

export default CheckoutPage;

function FulfillmentPanel({
  failed,
  onRefresh,
}: {
  failed: boolean;
  onRefresh: () => void | Promise<void>;
}) {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className='flex flex-col items-center gap-5 text-center py-8'>
      <div
        className='h-14 w-14 rounded-full flex items-center justify-center'
        style={{ background: failed ? '#FEF6EE' : '#ECFDF3' }}
      >
        {failed ? (
          <AlertTriangle size={24} style={{ color: 'var(--pp-warning)' }} />
        ) : (
          <Loader2 size={24} className='animate-spin' style={{ color: 'var(--pp-action)' }} />
        )}
      </div>
      <div>
        <h2 className='text-xl font-semibold' style={{ color: 'var(--pp-text)' }}>
          Payment received
        </h2>
        <p className='mt-2 text-sm max-w-md' style={{ color: 'var(--pp-text-muted)' }}>
          {failed
            ? 'The payment settled, but the app has not confirmed access yet. Refresh status or ask the app to retry fulfillment.'
            : 'Finalizing access with the app now. This usually takes a few seconds.'}
        </p>
      </div>
      <button
        type='button'
        onClick={handleRefresh}
        disabled={refreshing}
        className='inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold disabled:opacity-60'
        style={{ borderColor: 'var(--pp-border)', color: 'var(--pp-text)' }}
      >
        <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
        {refreshing ? 'Checking status…' : 'Refresh status'}
      </button>
    </div>
  );
}

function PaymentMethodChoice({
  amountUsd,
  merchant,
  onCrypto,
  onCard,
  openingCard,
}: {
  amountUsd: string;
  merchant: string;
  onCrypto: () => void;
  onCard: () => void;
  openingCard: boolean;
}) {
  return (
    <div className='flex flex-col gap-5'>
      <div>
        <h2 className='text-xl font-semibold' style={{ color: 'var(--pp-text)' }}>
          Choose how to pay
        </h2>
        <p className='mt-1 text-sm' style={{ color: 'var(--pp-text-muted)' }}>
          {merchant} accepts card checkout and crypto for this ${amountUsd} checkout.
        </p>
      </div>

      <div className='grid gap-3'>
        <button
          onClick={onCard}
          disabled={openingCard}
          className='group w-full rounded-lg border p-4 text-left transition-all disabled:cursor-not-allowed disabled:opacity-60'
          style={{ borderColor: 'var(--pp-border)', background: 'var(--pp-subtle)' }}
        >
          <div className='flex items-center gap-3'>
            <span
              className='h-11 w-11 rounded-full flex items-center justify-center'
              style={{ background: 'var(--pp-whop)', color: '#fff' }}
            >
              {openingCard ? <Loader2 size={20} className='animate-spin' /> : <CreditCard size={20} />}
            </span>
            <span className='min-w-0 flex-1'>
              <span className='block text-base font-semibold' style={{ color: 'var(--pp-text)' }}>
                Pay with card
              </span>
              <span className='mt-0.5 block text-sm' style={{ color: 'var(--pp-text-muted)' }}>
                Continue to Whop to complete card checkout.
              </span>
            </span>
            <ArrowRight size={18} style={{ color: 'var(--pp-text-subtle)' }} />
          </div>
        </button>

        <button
          onClick={onCrypto}
          className='group w-full rounded-lg border p-4 text-left transition-all'
          style={{ borderColor: 'var(--pp-border)', background: 'var(--pp-surface)' }}
        >
          <div className='flex items-center gap-3'>
            <span
              className='h-11 w-11 rounded-full flex items-center justify-center'
              style={{ background: 'var(--pp-action)', color: '#fff' }}
            >
              <Coins size={20} />
            </span>
            <span className='min-w-0 flex-1'>
              <span className='block text-base font-semibold' style={{ color: 'var(--pp-text)' }}>
                Pay with crypto
              </span>
              <span className='mt-0.5 block text-sm' style={{ color: 'var(--pp-text-muted)' }}>
                Use USDC, SOL, or USDT on Solana.
              </span>
            </span>
            <ArrowRight size={18} style={{ color: 'var(--pp-text-subtle)' }} />
          </div>
        </button>
      </div>
    </div>
  );
}
